import { ReactElement, ReactNode, useMemo } from 'react';
import {
    TasksAndAlertsUserActivityContext,
    TasksAndAlertsUserActivityContextData
} from './context';
import { useCompanyContext } from '@/companies/providers';
import { useTaskFilter } from '@/tasks/providers';
import { useFormatDate, useTimeSettings } from '@/common/hooks';
import {
    getEndOfDayInTimezone,
    getStartOfDayInTimezone
} from '@/common/util/format-date';
import { useFetchTasksAndAlertsUserActivity } from '../../queries';
import { buildAnalyticsQueryFilter } from '../../util/analytics-query-filter';
import { useReportsFilter } from '../reports-filter-provider';

type TasksAndAlertsUserActivityProviderProps = {
    readonly children?: ReactNode;
};

export function TasksAndAlertsUserActivityProvider({
    children
}: TasksAndAlertsUserActivityProviderProps): ReactElement {
    const { company } = useCompanyContext();
    const { teamIds, userIds, deviceIds } = useTaskFilter();
    const { range } = useReportsFilter();
    const { formatDateRange } = useFormatDate();
    const { timezone } = useTimeSettings();

    const query = useMemo(() => {
        return buildAnalyticsQueryFilter({
            companyId: company.id,
            teamIds,
            userIds,
            deviceIds,
            timezone,
            from: getStartOfDayInTimezone(range.from, timezone),
            to: getEndOfDayInTimezone(range.to, timezone)
        });
    }, [company.id, teamIds, userIds, deviceIds, range, timezone]);

    const { data, status } = useFetchTasksAndAlertsUserActivity(query);

    const dateString = useMemo(() => {
        if (data) {
            return formatDateRange(new Date(data.from), new Date(data.to));
        }
        return null;
    }, [data, formatDateRange]);

    const value: TasksAndAlertsUserActivityContextData = {
        status,
        activity: data?.items ?? [],
        dateString
    };

    return (
        <TasksAndAlertsUserActivityContext.Provider value={value}>
            {children}
        </TasksAndAlertsUserActivityContext.Provider>
    );
}
